'use client'

import { MentorExpertise, UserWithMentor } from '@/types/mentor'
import {
  Card,
  Text,
  Group,
  Badge,
  Button,
  Stack,
  Title,
  Tooltip,
} from '@mantine/core'
import Link from 'next/link'
import { Avatar } from './Avatar'
import classes from './MentorCard.module.css'
import { BADGE_DISPLAY } from '@/constants/badges'

interface MentorCardProps {
  user: UserWithMentor;
  priority?: boolean; // For LCP optimization on first card
}

export default function MentorCard({ user, priority = false }: MentorCardProps) {
  // Limit to first 5 badges, then show a "+N" overflow tag
  const VISIBLE_BADGE_COUNT = 5
  const { mentor } = user
  const visibleExpertises = mentor?.expertises.slice(0, VISIBLE_BADGE_COUNT) ?? []

  const handleClick = () => {
    // Mark that we're navigating from search and save scroll position
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('fromMentorSearch', 'true');
      sessionStorage.setItem('mentorSearchScrollY', window.scrollY.toString());
    }
  };

  return (
    <Link 
      href={`/mentors/${mentor?.handle ?? ''}`} 
      style={{ textDecoration: 'none' }}
      onClick={handleClick}
    >
      <Card
        padding="lg"
        radius="md"
        className="mentor-card"
        style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%', 
          cursor: 'pointer', 
          backgroundColor: 'white',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden'
        }}
      >
        {/* Avatar + basic info */}
        <Stack align="center" gap="sm" style={{ flex: 1 }}>
          <Avatar 
            radius={100} 
            size={140} 
            userId={user.id} 
            avatarId={user.avatarId} 
            className={classes.mentorAvatar}
            loading={priority ? "eager" : "lazy"}
            priority={priority}
          />

          <Stack gap="xs" align="center" style={{ textAlign: 'center', width: '100%' }}>
            <Title order={2}>
              {user.name}
            </Title>
            {mentor?.bio && (
              <Text
                size="sm"
                c="dimmed"
                style={{
                  // Clamp to 5 lines to keep cards equal height
                  display: '-webkit-box',
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  marginTop: '0.5rem',
                  marginBottom: '0.5rem',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  width: '100%',
                }}
              >
                {mentor.bio}
              </Text>
            )}
          </Stack>

          {/* Expertise badges */}
          <Group gap={4} justify="center" style={{ flexWrap: 'wrap', maxWidth: '100%' }}>
            {visibleExpertises.map((exp: MentorExpertise) => (
              <Badge
                key={exp.expertise.id}
                variant="light"
                size="xs"
                styles={{
                  root: { paddingLeft: 6, paddingRight: 6 },
                  label: { fontWeight: 600, letterSpacing: 0.2 },
                }}
                color="blue"
              >
                {exp.expertise.name}
              </Badge>
            ))}
            {mentor && mentor.expertises.length > VISIBLE_BADGE_COUNT && (
              <Badge
                variant="light"
                size="xs"
                styles={{ root: { paddingLeft: 6, paddingRight: 6 } }}
                color="blue"
              >
                +{mentor.expertises.length - VISIBLE_BADGE_COUNT}
              </Badge>
            )}
          </Group>

          <Group gap={4} justify="center" style={{ flexWrap: 'wrap', maxWidth: '100%' }}>
            {/* User badges */}
            {(user as any).badges?.map((badge: string) => {
              const badgeConfig = BADGE_DISPLAY[badge];
              if (!badgeConfig) return null;
              
              return (
                <Tooltip 
                  key={badge}
                  label={badgeConfig.description}
                  multiline
                  w={240}
                  withArrow
                  position="bottom"
                  transitionProps={{ transition: 'fade', duration: 200 }}
                >
                  <Badge
                    variant="dot"
                    size="xs"
                    color={badgeConfig.color}
                    styles={{
                      root: { paddingLeft: 6, paddingRight: 6, cursor: 'help' },
                      label: { fontWeight: 600 },
                    }}
                  >
                    {badgeConfig.label}
                  </Badge>
                </Tooltip>
              );
            })}
          </Group>
        </Stack>

        <Button variant="light" color="navy" fullWidth mt="md">
          View Profile
        </Button>
      </Card>
    </Link>
  )
}